import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  faceBookCallBack,
  getAllCompanies,
  getAllFBPages,
  createFBPage
} from '../services/api/implementation/impl';
import { useNavigate } from 'react-router-dom';
import Play from '../../shared/assets/svg/play.svg';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { Check } from 'lucide-react';

/** Required for subscribed_apps webhooks and Instant Forms; keep in sync with Meta app permissions. */
const FACEBOOK_OAUTH_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'leads_retrieval',
  'pages_manage_ads',
  'business_management'
].join(',');

const FB_RECONNECT_KEY = 'fbReconnect';
const FB_LINKED_PAGE_ID_KEY = 'fbLinkedPageId';

const clearReconnectFlags = () => {
  localStorage.removeItem(FB_RECONNECT_KEY);
  localStorage.removeItem(FB_LINKED_PAGE_ID_KEY);
};

/** Prefer API fields from Graph /me (name → fb_user_name) when backend returns them. */
const getFbUserName = (source) => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
  return (
    source.fb_user_name ||
    source.facebook_user_name ||
    source.fbUserName ||
    source.user?.name ||
    source.me?.name ||
    null
  );
};

const getPagesFromTokenResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.pages)) return data.pages;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

/** company_logo is an array of { name, url } from the company API. */
const getCompanyLogoUrl = (company) => {
  const logo = company?.company_logo;
  if (Array.isArray(logo)) return logo[0]?.url || null;
  if (typeof logo === 'string' && logo) return logo;
  return null;
};

const getCompanyInitials = (name) =>
  (name || 'CO')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const SetupStep = ({ index, done, current, label, detail }) => (
  <li className="flex items-start gap-2.5">
    <span
      className={`mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${
        done
          ? 'border-[#0F766E] bg-[#0F766E] text-white'
          : current
            ? 'border-primary bg-white text-primary'
            : 'border-[#E5EAF1] bg-[#F7F9FA] text-[#9CA3AF]'
      }`}>
      {done ? <Check className="h-3 w-3" strokeWidth={3} /> : index}
    </span>
    <div className="min-w-0">
      <p
        className={`text-sm leading-tight ${
          done || current ? 'font-medium text-[#111827]' : 'text-[#6B7280]'
        }`}>
        {label}
      </p>
      {detail ? <p className="mt-0.5 text-xs text-[#6B7280]">{detail}</p> : null}
    </div>
  </li>
);

const TextAction = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1 text-xs font-medium text-[#4D6C72] transition-colors hover:text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline">
    {children}
  </button>
);

const FacebookCompanies = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [OpenViewPages, setOpenViewPages] = useState(false);
  const [code, setCode] = useState(searchParams.get('code'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pages, setPages] = useState([]);
  const { collapsed } = useSidebar();
  const [selectedPage, setSelectedPage] = useState({
    page_id: null,
    name: null,
    pageAccessToken: null,
    company_id: null
  });

  useEffect(() => {
    mixpanel.track('Facebook Companies Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: companiesData, refetch } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  const runReconnectSoftCheck = async (companyId) => {
    const linkedPageId = localStorage.getItem(FB_LINKED_PAGE_ID_KEY);
    if (!linkedPageId) return;

    try {
      const pagesList = await getAllFBPages(companyId);
      const pageFound = (pagesList || []).some(
        (page) => String(page.id) === String(linkedPageId)
      );
      if (!pageFound) {
        toast.error(
          "Your linked Facebook Page wasn't found. You may have signed in with a different Facebook account."
        );
      }
    } catch (err) {
      console.error('Error during reconnect page soft check', err);
    }
  };

  const { mutate: handleAction, isPending: isTokenPending } = useMutation({
    mutationFn: ({ body }) => faceBookCallBack(body),
    onSuccess: async (data, variables) => {
      const isReconnect = localStorage.getItem(FB_RECONNECT_KEY) === '1';
      const companyId = variables?.body?.company_id;
      const fbUserName = getFbUserName(data);

      if (fbUserName && companyId) {
        setCompanies((prev) =>
          prev.map((company) =>
            company.id === companyId
              ? { ...company, fb_user_name: fbUserName, facebook_user_name: fbUserName }
              : company
          )
        );
      }

      if (isReconnect) {
        toast.success(
          fbUserName
            ? `Facebook permissions updated for ${fbUserName}. Use Select Page if you need to re-link a page.`
            : 'Facebook permissions updated. Use Select Page if you need to re-link a page.'
        );
        if (companyId) {
          await runReconnectSoftCheck(companyId);
        }
      } else {
        toast.success(
          fbUserName
            ? `Facebook connected successfully as ${fbUserName}`
            : 'Facebook connected successfully'
        );
      }

      setPages(getPagesFromTokenResponse(data));
      clearReconnectFlags();
      refetch();
    },
    onError: (err) => {
      console.error(`Error Generating AccessToken`, err);
      clearReconnectFlags();
      toast.error(err?.response?.data?.message || err?.message || `Error Generating AccessToken`);
    }
  });

  useEffect(() => {
    const storedCompanyId = localStorage.getItem('companyId');
    if (code) {
      if (storedCompanyId) {
        const body = {
          company_id: parseInt(storedCompanyId, 10),
          code: code
        };
        handleAction({ body });
        setCode(null);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('code');
        setSearchParams(newSearchParams);
      }
    }
  }, [code]);

  useEffect(() => {
    if (companiesData) {
      setCompanies((prev) =>
        companiesData.map((company) => {
          const apiName = getFbUserName(company);
          if (apiName) return company;
          const prevName = getFbUserName(prev.find((c) => c.id === company.id));
          if (!prevName) return company;
          return {
            ...company,
            fb_user_name: prevName,
            facebook_user_name: prevName
          };
        })
      );
    }
  }, [companiesData]);

  const goToInstantForms = (company) => {
    navigate('/builder/integration/facebook-companies/pages', {
      state: {
        pageID: company.page?.id,
        cmpId: company.id
      }
    });
  };

  const getPrimaryCta = (company) => {
    const pageLinked = Boolean(company.page?.is_connected_to_company);
    const fbConnected = Boolean(company.isConnectedToFb);

    if (!fbConnected) {
      return {
        label: 'Connect Facebook',
        onClick: () => handleFacebookLogin(company.id)
      };
    }
    if (!pageLinked) {
      return {
        label: 'Select Page',
        onClick: () => AllPages({ id: company.id })
      };
    }
    return {
      label: 'Instant Forms',
      onClick: () => goToInstantForms(company)
    };
  };

  const handleFacebookLogin = (companyId, { reconnect = false, linkedPageId = null } = {}) => {
    const appId = '9566903480068710';
    const redirectUri =
      'https://app.housingram.com/builder/integration/facebook-companies';

    const params = new URLSearchParams({
      client_id: appId,
      redirect_uri: redirectUri,
      scope: FACEBOOK_OAUTH_SCOPES,
      response_type: 'code'
    });

    if (reconnect) {
      params.set('auth_type', 'rerequest');
      localStorage.setItem(FB_RECONNECT_KEY, '1');
      if (linkedPageId) {
        localStorage.setItem(FB_LINKED_PAGE_ID_KEY, String(linkedPageId));
      } else {
        localStorage.removeItem(FB_LINKED_PAGE_ID_KEY);
      }
    } else {
      clearReconnectFlags();
    }

    localStorage.setItem('companyId', companyId);
    window.location.href = `https://www.facebook.com/v17.0/dialog/oauth?${params.toString()}`;
  };

  const { mutate: AllPages } = useMutation({
    mutationFn: ({ id }) => getAllFBPages(id),
    onSuccess: async (data, { id }) => {
      setPages(data);
      setSelectedPage((prev) => ({ ...prev, company_id: id }));
      setOpenViewPages(true);
    },
    onError: (err) => {
      console.error(`Error Fetching FB Pages`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Fetching FB Pages`);
    }
  });

  const HandleCreatePage = () => {
    if (!selectedPage.page_id) {
      toast.error('Click On Some Page To Select');
      return;
    }
    handleCreatePage({ selectedPage });
  };

  const { mutate: handleCreatePage } = useMutation({
    mutationFn: ({ selectedPage }) => createFBPage(selectedPage),
    onSuccess: async () => {
      toast.success(`Page Connected Successfully`);
      setSelectedPage({
        page_id: null,
        name: null,
        pageAccessToken: null,
        company_id: null
      });
      setOpenViewPages(false);
      refetch();
    },
    onError: (err) => {
      console.error(`Error Connecting Page`, err);
      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes('already associated with another company')
      ) {
        toast.error('Selected Company is already linked with another company');
      } else {
        toast.error(err?.response?.data?.message || err?.message || `Error Connecting Page`);
      }
    }
  });

  const isFbActionDisabled = isSubmitting || isTokenPending;

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration')}
          data={{ name: 'Integrations > Facebook' }}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {companies.map((company) => {
              const fbUserName = getFbUserName(company);
              const pageLinked = Boolean(company.page?.is_connected_to_company);
              const fbConnected = Boolean(company.isConnectedToFb);
              const step1Done = fbConnected;
              const step2Done = pageLinked;
              const primary = getPrimaryCta(company);
              const logoUrl = getCompanyLogoUrl(company);
              const doneCount = [step1Done, step2Done].filter(Boolean).length;
              const currentStep = !step1Done ? 1 : !step2Done ? 2 : 0;
              const statusBadge = !fbConnected
                ? { label: 'Not connected', className: 'bg-[#F3F4F6] text-[#6B7280]' }
                : { label: 'Connected', className: 'bg-[#EAF8F2] text-[#0F766E]' };

              return (
                <div
                  key={company.id}
                  className="group flex flex-col gap-4 rounded-2xl border border-[#E5EAF1] bg-white p-5 shadow-sm transition-all duration-300 ease-out hover:border-[#D8DEE6] hover:shadow-md">
                  <div className="flex items-start gap-3">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={`${company.company_name || 'Company'} logo`}
                        className="h-12 w-12 shrink-0 rounded-xl border border-[#E5EAF1] bg-[#F2F5F8] object-contain p-1"
                      />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#BFE6DE] bg-[#E7F2EF] text-base font-semibold text-[#2A565E]">
                        {getCompanyInitials(company.company_name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-[17px] font-semibold leading-tight text-[#111827]"
                        title={company.company_name}>
                        {company.company_name}
                      </p>
                      <p className="mt-0.5 truncate text-sm text-[#6B7280]">
                        {company.company_type || 'Company'}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBadge.className}`}>
                      {statusBadge.label}
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs font-medium text-[#6B7280]">
                      <span>Setup progress</span>
                      <span>{doneCount} of 2</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#EDF1F4]">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${(doneCount / 2) * 100}%` }}
                      />
                    </div>
                  </div>

                  <ol className="flex flex-col gap-3">
                    <SetupStep
                      index={1}
                      done={step1Done}
                      current={currentStep === 1}
                      label="Facebook connected"
                      detail={
                        fbConnected && fbUserName
                          ? `Connected as ${fbUserName}`
                          : null
                      }
                    />
                    <SetupStep
                      index={2}
                      done={step2Done}
                      current={currentStep === 2}
                      label="Page selected"
                      detail={
                        fbConnected && !pageLinked
                          ? 'Select a Page to finish'
                          : pageLinked
                            ? 'Ready to map Instant Forms'
                            : null
                      }
                    />
                  </ol>

                  <div className="mt-auto flex flex-col gap-3 border-t border-[#EEF1F4] pt-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Button
                          displayType="full-w-primary"
                          onClick={() => primary.onClick?.()}
                          disabled={isFbActionDisabled || primary.disabled || !primary.onClick}>
                          {primary.label}
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <TextAction>
                        <img src={Play} alt="" className="h-3 w-3" />
                        Demo
                      </TextAction>
                      {company.isConnectedToFb ? (
                        <TextAction
                          disabled={isFbActionDisabled}
                          onClick={() =>
                            handleFacebookLogin(company.id, {
                              reconnect: true,
                              linkedPageId: pageLinked ? company.page?.id : null
                            })
                          }>
                          Reconnect
                        </TextAction>
                      ) : null}
                      {step2Done ? (
                        <TextAction
                          disabled={isFbActionDisabled}
                          onClick={() => goToInstantForms(company)}>
                          Advanced
                        </TextAction>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {OpenViewPages && (
        <Modal
          isDirty={true}
          width={60}
          name="FaceBook Pages"
          onClose={() => {
            setOpenViewPages(false);
            setSelectedPage({
              page_id: null,
              name: null,
              pageAccessToken: null,
              company_id: null
            });
          }}>
          <div className="flex flex-col ">
            <div className="grid grid-cols-3 gap-6">
              {pages.map((page) => (
                <div
                  onClick={() =>
                    setSelectedPage((prev) =>
                      prev.page_id === page.id
                        ? { ...prev, page_id: null, name: null, pageAccessToken: null }
                        : {
                            ...prev,
                            page_id: page.id,
                            name: page.name,
                            pageAccessToken: page.access_token
                          }
                    )
                  }
                  key={page.id}
                  className={`bg-white shadow-md p-6 rounded-lg flex flex-col cursor-pointer ${
                    selectedPage.page_id === page.id ? 'border-2 border-green-500' : ''
                  }`}>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-lg">{page.name}</h3>
                    <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium ">
                      <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                      <p>Live</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => {
                  setOpenViewPages(false);
                  setSelectedPage({
                    page_id: null,
                    name: null,
                    pageAccessToken: null,
                    company_id: null
                  });
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} onClick={HandleCreatePage}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default FacebookCompanies;
